import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  CoachingCaseCard,
  CoachingCaseFormat,
  CoachingDrillResult,
} from '@euler/model/coaching';
import {
  CoachingPracticeReportView,
  CoachingReplaySummary,
} from '@euler/model/coaching-practice';
import { memo } from 'react';
import { ScrollView, View, useWindowDimensions } from 'react-native';
import { CardNotice } from '../../../ai-suite/components/ChatCard';
import { MilestoneBanner } from '../../progress/components/MilestoneBanner';
import { AgainButtons } from './components/AgainButtons';
import { AreaSection } from './components/AreaSection';
import { CriteriaSection } from './components/CriteriaSection';
import { CriticalErrors } from './components/CriticalErrors';
import { DrillVerdict } from './components/DrillVerdict';
import { MomentCard } from './components/MomentCard';
import { NextPracticeCard } from './components/NextPracticeCard';
import { NotesSection } from './components/NotesSection';
import { ReportHeading } from './components/ReportHeading';
import { ReportOutcome } from './components/ReportOutcome';
import { TranscriptLink } from './components/TranscriptLink';

export interface ReportActions {
  onRetryMoment: (turn: number) => void;
  onAgain: (levelUp: boolean) => void;
  againBusy: 'again' | 'level_up' | null;
  againFailed: boolean;
  onPractise: (card: CoachingCaseCard) => void;
  onOpenTranscript: () => void;
}

/** The latest replay of each moment. */
function latestReplays(replays: CoachingReplaySummary[]) {
  const byTurn = new Map<number, CoachingReplaySummary>();
  for (const x of replays) {
    const seen = byTurn.get(x.momentTurn);
    if (!seen || x.replayId > seen.replayId) byTurn.set(x.momentTurn, x);
  }
  return byTurn;
}

/**
 * A practice's report, top to bottom: the outcome and stars, critical
 * errors, the areas, each criterion, moments to redo, what went well and
 * what could, and what to practise next. A drill reads as a short verdict.
 */
export const ReportView = memo(
  ({
    report,
    replays,
    actions,
  }: {
    report: CoachingPracticeReportView;
    replays: CoachingReplaySummary[];
    actions: ReportActions;
  }) => {
    const { rpx } = useResponsiveContext();
    const { width } = useWindowDimensions();
    const wide = width >= rpx(720);
    const drill = report.format === CoachingCaseFormat.Drill;
    const replayed = latestReplays(replays);
    const again = (
      <View style={{ gap: rpx(8) }}>
        {actions.againFailed ? (
          <CardNotice tone="danger" text="没能开始，请稍后再试。" />
        ) : null}
        <AgainButtons
          againTitle={
            drill
              ? report.end.drillResult === CoachingDrillResult.Demonstrated
                ? '换个客户再试'
                : '再试一次'
              : '再练一次'
          }
          levelUp={report.levelUp}
          busy={actions.againBusy}
          onAgain={actions.onAgain}
        />
      </View>
    );
    return (
      <ScrollView
        contentContainerStyle={{
          width: '100%',
          maxWidth: rpx(860),
          alignSelf: 'center',
          paddingHorizontal: rpx(16),
          paddingTop: rpx(16),
          paddingBottom: rpx(32),
          gap: rpx(20),
        }}
      >
        <ReportOutcome report={report} />
        {report.milestones.length ? (
          <MilestoneBanner milestones={report.milestones} />
        ) : null}
        {report.criticalErrors.length ? (
          <CriticalErrors errors={report.criticalErrors} />
        ) : null}
        {drill ? (
          <DrillVerdict
            criterion={report.criteria[0] ?? null}
            betterLine={report.drillBetterLine}
          />
        ) : (
          <>
            {report.areas.length ? (
              <AreaSection
                areas={report.areas}
                previous={report.previousAreas}
                wide={wide}
              />
            ) : null}
            {report.criteria.length ? (
              <CriteriaSection
                criteria={report.criteria}
                areas={report.areas}
              />
            ) : null}
            {!report.exam && report.moments.length ? (
              <View style={{ gap: rpx(12) }}>
                <ReportHeading title="值得重练的时刻" />
                {report.moments.map(x => (
                  <MomentCard
                    key={x.turn}
                    moment={x}
                    customerName={report.customer.name}
                    replay={replayed.get(x.turn) ?? null}
                    onRetry={() => actions.onRetryMoment(x.turn)}
                  />
                ))}
              </View>
            ) : null}
            <NotesSection
              strengths={report.strengths}
              missed={report.missedOpportunities}
            />
          </>
        )}
        {report.exam ? null : again}
        {!report.exam && report.nextPractice ? (
          <NextPracticeCard
            suggestion={report.nextPractice}
            onPress={() => actions.onPractise(report.nextPractice!.case)}
          />
        ) : null}
        <TranscriptLink onPress={actions.onOpenTranscript} />
      </ScrollView>
    );
  },
);
